/**
 * Convert line breaks to HTML breaks for display in UI
 * @param text - Text that may contain line breaks
 * @returns Text with line breaks converted to <br/> tags
 */
export const nl2br = (text: string): string => {
  return text && text.replace(/\n/g, '<br/>');
};

/**
 * Gets the day of week (0-6) from a date string in format DD.MM.YYYY
 * @param dateString - Date string in format DD.MM.YYYY
 * @returns Day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
 */
export const getDayOfWeekFromDateString = (dateString: string): number => {
  if (!dateString) return -1;
  
  // Handle date in format DD.MM.YYYY
  if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(dateString)) {
    try {
      const [day, month, year] = dateString.split('.').map(Number);
      const date = new Date(year, month - 1, day);
      return date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    } catch (e) {
      console.error('Error parsing date:', dateString, e);
      return -1;
    }
  }
  
  return -1;
};

export interface LunchStoreInfo {
  id: number;
  name: string;
  spaceCode?: string;
}

export interface DailyLunchMenu {
  day: string;
  lunchMenu: string;
}

export interface LunchItem {
  store: LunchStoreInfo;
  dailyLunchMenus: DailyLunchMenu[];
  generalInformation: string;
  lunchTime: string;
}

export interface StoresLunchItem {
  lunchMenu: string;
  store: LunchStoreInfo;
  generalInformation: string;
  lunchTime: string;
}

/**
 * Filters lunch data to return only menus for the specified day of week
 * @param lunch - Array of lunch items from API
 * @param dayOfWeek - Day of week index (0-6, where 0 is Sunday)
 * @returns Array of filtered lunch items for the specified day
 */
export function getLunchByStore(lunch: LunchItem[], dayOfWeek: number): StoresLunchItem[] {
  // Return empty array for invalid input
  if (!lunch || !Array.isArray(lunch) || lunch.length === 0) {
    return [];
  }

  const result: StoresLunchItem[] = [];
  
  // Process each lunch item
  for (const lunchItem of lunch) {
    // Skip invalid items
    if (!lunchItem?.dailyLunchMenus?.length) continue;
    
    // Find menus for the specified day
    for (const dailyMenu of lunchItem.dailyLunchMenus) {
      if (!dailyMenu?.day) continue;
      
      // Get day of week from date string
      const menuDay = getDayOfWeekFromDateString(dailyMenu.day);
      
      // If this menu is for the requested day, add it to results
      if (menuDay === dayOfWeek) {
        result.push({
          lunchMenu: dailyMenu.lunchMenu,
          store: lunchItem.store,
          generalInformation: lunchItem.generalInformation,
          lunchTime: lunchItem.lunchTime
        });
        break; // Only add one menu per store
      }
    }
  }
  
  return result;
}
  